<script lang="ts">
export default {
  name: 'WizardStepper',
};
</script>

<script setup lang="ts">
import { Check, TriangleAlert } from '@lucide/vue';

interface WizardStep {
  key: string;
  label: string;
  // Compact variant shown in the stepper; falls back to label.
  shortLabel?: string;
}

const props = defineProps<{
  steps: WizardStep[];
  currentStep: number;
  // Outstanding issue count per step (aligned with `steps`). A completed step
  // with issues renders red instead of the green check; null = unknown.
  stepIssues?: (number | null)[];
  // Furthest step index the user has reached. Steps up to this index keep
  // their done/done-issues state even when they're ahead of the current step
  // (i.e. after navigating back). Omitted = positional behavior only.
  completedThrough?: number;
}>();

const emit = defineEmits<{
  stepClick: [index: number];
}>();

function stateFor(index: number): 'done' | 'done-issues' | 'active' | 'idle' {
  if (index === props.currentStep) return 'active';
  if (index < props.currentStep || index <= (props.completedThrough ?? -1)) {
    const issues = props.stepIssues?.[index];
    return issues != null && issues > 0 ? 'done-issues' : 'done';
  }
  return 'idle';
}

function handleClick(index: number) {
  if (index === props.currentStep) return;
  emit('stepClick', index);
}
</script>

<template>
  <div class="flex items-center gap-0">
    <template
      v-for="(step, index) in steps"
      :key="step.key">
      <div
        class="flex shrink-0 items-center gap-2"
        :class="stateFor(index) === 'active' ? 'cursor-default' : 'cursor-pointer'"
        @click="handleClick(index)">
        <div
          class="flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors"
          :data-step-state="stateFor(index)"
          :class="{
            'border border-success bg-success/10 text-success': stateFor(index) === 'done',
            'border border-error bg-error/10 text-error': stateFor(index) === 'done-issues',
            'border-2 border-primary bg-primary/10 text-primary': stateFor(index) === 'active',
            'border border-base-300 bg-base-200 text-base-content/40': stateFor(index) === 'idle',
          }">
          <Check
            v-if="stateFor(index) === 'done'"
            :size="13" />
          <TriangleAlert
            v-else-if="stateFor(index) === 'done-issues'"
            :size="13" />
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div
          class="text-xs whitespace-nowrap"
          :class="{
            'font-semibold text-base-content': stateFor(index) === 'active',
            'text-base-content/60': stateFor(index) !== 'active',
          }">
          {{ step.shortLabel ?? step.label }}
        </div>
      </div>
      <div
        v-if="index < steps.length - 1"
        class="mx-3 h-px min-w-6 flex-1 bg-base-300" />
    </template>
  </div>
</template>
